import { useQuery } from '@tanstack/react-query';

import { getHttpClient, useHttpClient } from '../lib/http-client';
import { getQueryClient } from '../lib/query-client';
import { headers } from './meta';

enum QueryKey {
	QualityScores = 'recognition-analysis:quality-scores',
	QualitySuggestions = 'recognition-analysis:suggestions',
}

export const qualityScoresQueryKey = (text?: string) => [QueryKey.QualityScores, text].filter(Boolean);
export const qualitySuggestionsQueryKey = (params: { dimensionKey: string; text: string }) => [QueryKey.QualitySuggestions, params];

export const qualityScoresPath = (prefix = '') => `${prefix}/recognition/quality_scores`;
export const qualitySuggestionsPath = (prefix = '') => `${prefix}/recognition/quality_suggestions`;

export enum DimensionKey {
	Specific = 'specific',
	Contextual = 'contextual',
	ValuesAligned = 'values_aligned',
	Personal = 'personal',
	Genuine = 'genuine',
}

export type QualityScoreAPIData = {
	version: number;
	dimensions: Array<{
		key: DimensionKey;
		reason: string;
		matches: boolean;
		examples: string[];
	}>;
};

export const loadQualityScore = async (text: string) => {
	const http = await getHttpClient();
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: qualityScoresQueryKey(text),
		queryFn: async () => {
			return (await http.post<QualityScoreAPIData>(qualityScoresPath(), { bonusText: text }, { headers })).data;
		},
		retry: false,
	});
};

export const getQualityScores = async () => {
	const queryClient = await getQueryClient();
	return queryClient.getQueriesData<QualityScoreAPIData>(qualityScoresQueryKey());
};

type QualitySuggestionsAPIData = {
	version: number;
	key: DimensionKey;
	matches: boolean;
	suggestions: string[];
};

export const useQualitySuggestions = ({ dimensionKey, text }: { dimensionKey: DimensionKey; text: string }) => {
	const http = useHttpClient();

	return useQuery({
		enabled: !!text,
		queryKey: qualitySuggestionsQueryKey({ dimensionKey, text }),
		queryFn: async () => {
			return (await http.post<QualitySuggestionsAPIData>(qualitySuggestionsPath(), { bonusText: text, dimensionKey }, { headers })).data;
		},
		retry: false,
	});
};
